<template>
    <SectionViewLayout
        title='settings.smokescreen_addresses.title.main'
        :content_state='smokescreen_controller.content_state'
        content_class="settings-content-list"
        close_route="settings"
    >
        <template #actions>
            <ActionButton
                @click="onSearchToggle()"
                v-tooltip='"settings.smokescreen_addresses.actions.search"'
                icon='fas fa-search'
                :class="{'active' : search_active}"
            />
            <RouterButton v-if='disable_add === false'
                link='/settings/smokescreen_addresses/add'
                v-tooltip='"settings.smokescreen_addresses.actions.add"'
                icon="fas fa-plus-circle"
            />
        </template>

        <template #pre_content>
            <InputSearch v-if="search_active"
                ref="input_search_component"
                class="contacts-search"
                @inputSearchSet='onInputSearchSet'
                @InputSearchClear='onInputSearchClear'
            />
        </template>

        <template #content>
            <form>
                <fieldset v-if="AccountController.account.premium.upgrade">
                    <PremiumSubscribe
                        :text="Language.say('settings.smokescreen_addresses.premium.disabled', [AccountController.account.cos.max_smokescreen_addresses])"
                        :gtm="{
                            module : 'smokescreen_settings',
                            feature: 'smokescreen_list',
                            block  : 'pre_content',
                            action : 'premium_subscribe'
                        }"
                    />
                </fieldset>

                <fieldset class="bottom-space">
                    <p>
                        {{ Language.say('settings.smokescreen_addresses.content.sub_01') }}
                    </p>
                    <p>
                        {{ Language.say('settings.smokescreen_addresses.content.sub_02') }}
                    </p>

                    <ContainerAlert
                        :type="disable_add ? 'warning' : 'info'"
                    >
                        {{Language.say('settings.smokescreen_addresses.notices.usage_1')}}
                        <strong> {{ addresses_list.count  }}</strong> {{Language.say('settings.smokescreen_addresses.notices.usage_2')}}
                        <strong> {{  AccountController.account.cos.max_smokescreen_addresses }}</strong> {{Language.say('settings.smokescreen_addresses.notices.usage_3')}}
                    </ContainerAlert>

                    <div class="top-space control-group" v-if="addresses_list.count">
                        <div class="control item-list">
                            <div class="item">
                                <span>
                                    {{ Language.say("settings.smokescreen_addresses.content.addresses") }}
                                </span>
                                <span>
                                    {{ Language.say("settings.smokescreen_addresses.content.status") }}
                                </span>
                            </div>

                            <SmartList
                                @smartListItemsSet=onSmartListItemsSet
                                :smart_list_items="addresses_list.items">
                                    <template #elements>
                                        <div v-for="(item, index) in smart_list_items"
                                            :key="item.address"
                                            class="item"
                                        >
                                            <span>
                                                {{item.address}}
                                            </span>
                                            <span>
                                                <template v-if="!clipboard_copy[item.address]">
                                                    <i class="fas fa-copy" v-tooltip="'global.actions.copy_clipboard'"
                                                        @click.prevent="onCopyToClipboard(item.address)"
                                                    />
                                                </template>

                                                <template v-else>
                                                    <small>
                                                        {{ Language.say("settings.smokescreen_addresses.content.clipboard_copied") }}
                                                    </small>
                                                    <i class="fas fa-check" v-tooltip="'global.actions.copied_to_clipboard'" />
                                                </template>
                                            </span>
                                            <span>
                                                <InputToggle
                                                    :id="'snsc_tg_' + index"
                                                    :active="item.active"
                                                    @click.prevent="onToggleEnable(item)"
                                                    :loading="props.smokescreen_controller.saving_state.saving[item.address]"
                                                />
                                            </span>
                                        </div>
                                    </template>
                            </SmartList>
                        </div>
                    </div>
                    <div class="top-space" v-if="addresses_list.count === 0">
                        <p>{{ Language.say("settings.smokescreen_addresses.content.list_empty") }}</p>
                    </div>
                </fieldset>
            </form>
        </template>
    </SectionViewLayout>
</template>

<script setup>
    import {reactive, onBeforeMount, ref} from 'vue';
    import SectionViewLayout              from '@/vue/Layouts/Sections/SectionViewLayout.vue';
    import ActionButton                   from '@/vue/Elements/Buttons/ActionButton.vue';
    import RouterButton                   from '@/vue/Elements/Buttons/RouterButton.vue';
    import InputSearch                    from '@/vue/Elements/Inputs/InputSearch.vue';
    import InputToggle                    from '@/vue/Elements/Inputs/InputToggle.vue';
    import ContainerAlert                 from '@/vue/Elements/Content/ContainerAlert.vue';
    import SmartList                      from '@/vue/Elements/Tools/SmartList.vue';
    import AccountController              from '@/js/Webmail/AccountController';
    import SmokescreenController          from '@/js/Settings/Smokescreen/SmokescreenController';
    import Language                       from '@/js/Shared/Language/Language.js';
    import PremiumSubscribe               from '../../Webmail/Partials/Premium/PremiumSubscribe.vue';

    const props = defineProps({
        smokescreen_controller: SmokescreenController,
    });

    const smart_list_items = ref();
    const search_active    = ref(false);
    const addresses_list   = ref({});
    const disable_add      = ref(false);
    const clipboard_copy   = ref({});

    props.smokescreen_controller.content_state.onErrorRetry(async () => {
        await fetchSmokescreenAddresses();
        setAddressesList();
        setDisableAdd();
    })

    onBeforeMount(async () => {
        await fetchSmokescreenAddresses();
        setAddressesList();
        setDisableAdd();
    })

    const onCopyToClipboard = async (address) => {
        copyToClypboard(address);
    }

    const fetchSmokescreenAddresses = async () => {
        await props.smokescreen_controller.fetchSmokescreenList()
    }

    const onToggleEnable = async (item) => {
        if (item.active === true) {
            await props.smokescreen_controller.disableSmokescreenAddress(item);
            return;
        }

        await props.smokescreen_controller.enableSmokescreenAddress(item);
    }

    const onSmartListItemsSet = (items) => {
        setSmartListItems(items);
    }

    const onSearchToggle = () => {
        search_active.value = !search_active.value;
    }

    const onInputSearchSet = (search_value) => {
        props.smokescreen_controller.data.search(search_value);
        setSearchList();
    }

    const onInputSearchClear = () => {
        props.smokescreen_controller.data.search_list.reset();
        setAddressesList();
    }

    const copyToClypboard = async (text) => {
        try {
            navigator.clipboard.writeText(text)
            clipboard_copy.value[text] = true;

            setTimeout(() => {
                clipboard_copy.value[text] = false;
            }, 2500);
        } catch (err) {
            // Ignore browser clipboard errors
        }
    }

    const setSmartListItems = (items) => {
        smart_list_items.value = items;
    }

    const setAddressesList = () => {
        addresses_list.value = props.smokescreen_controller.data;
    }

    const setSearchList = () => {
        addresses_list.value = props.smokescreen_controller.data.search_list;
    }

    const setDisableAdd = () => {
        if (props.smokescreen_controller.data.count >= AccountController.account.cos.max_smokescreen_addresses) {
            disable_add.value = true;
            return;
        }

        disable_add.value = false;
    }
</script>
